import type { AiUsageView } from '@fos/shared/contracts/copilot';
import { aiJobKindLabel } from '@fos/shared/labels';
import { Dec } from '@fos/shared/money';
import { todayLocal } from '@fos/shared/time';
import { CircleAlert, Sparkles, Wallet } from 'lucide-react';
import { useState } from 'react';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Select } from '../../../ui/Select';
import { Stat } from '../../../ui/Stat';
import { ErrorState, Skeleton } from '../../../ui/States';
import { Table, TBody, Td, THead, Th, Tr } from '../../../ui/Table';
import { formatAiCost } from '../../copilot/copilot-ui';
import { monthLabel, recentMonths } from './ai-settings-ui';

const JOB_FORMS = ['zadanie', 'zadania', 'zadań'] as const;

/**
 * Zużycie AI w miesiącu (Europe/Warsaw): liczba zadań AI, koszt (z „Widzi stawki i kwoty”), błędy i przekroczenia
 * budżetu, zadania w symulacji; podział na rodzaje i osoby. Właściciel, admin i manager widzą cały workspace,
 * pozostali — swoje zużycie i swój limit.
 */
export function AiUsageSection() {
  const months = recentMonths(todayLocal());
  const [month, setMonth] = useState(months[0] as string);
  const usage = trpc.ai.usage.useQuery({ month });

  return (
    <Section aria-label="Zużycie AI" className="flex flex-col gap-4">
      <SectionHeader
        tone="strong"
        title={usage.data?.scope === 'mine' ? 'Twoje zużycie AI' : 'Zużycie AI'}
        action={
          <Select
            size="s"
            aria-label="Miesiąc"
            className="w-44"
            value={month}
            onValueChange={setMonth}
            options={months.map((m) => ({ value: m, label: monthLabel(m) }))}
          />
        }
      />
      {usage.isPending ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[110px] rounded-xl" />
          ))}
        </div>
      ) : usage.isError ? (
        <ErrorState compact onRetry={() => void usage.refetch()} retrying={usage.isFetching} />
      ) : (
        <UsageBody usage={usage.data} />
      )}
    </Section>
  );
}

function UsageBody({ usage }: { usage: AiUsageView }) {
  const showMoney = usage.costPln !== undefined;
  const budget = usage.budgetPln ?? null;
  const progress =
    showMoney && budget && new Dec(budget).gt(0)
      ? Math.min(1, new Dec(usage.costPln ?? '0').div(budget).toNumber())
      : undefined;
  const problems = usage.failed + usage.budgetExceeded;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Stat
          label="Zadania AI"
          icon={Sparkles}
          value={usage.jobs}
          note={usage.simulatedJobs > 0 ? `${usage.simulatedJobs} w symulacji` : 'w tym miesiącu'}
          showDot={false}
        />
        {showMoney && (
          <Stat
            label="Koszt"
            icon={Wallet}
            value={formatAiCost(usage.costPln)}
            progress={progress}
            tone={progress !== undefined && progress >= 0.8 ? 'warning' : 'neutral'}
            note={budget ? `z ${formatAiCost(budget)}` : 'bez limitu'}
            showDot={false}
          />
        )}
        <Stat
          label="Błędy i limity"
          icon={CircleAlert}
          value={problems}
          tone={usage.budgetExceeded > 0 ? 'warning' : problems > 0 ? 'danger' : 'success'}
          note={
            usage.budgetExceeded > 0
              ? `limit kosztów zatrzymał AI: ${usage.budgetExceeded}`
              : problems > 0
                ? `z błędem: ${problems} ${plural(problems, JOB_FORMS)}`
                : 'bez błędów'
          }
        />
      </div>

      {usage.byKind.length > 0 && (
        <Table aria-label="Zużycie według rodzaju">
          <THead>
            <Tr>
              <Th>Rodzaj</Th>
              <Th align="right">Zadania</Th>
              {showMoney && <Th align="right">Koszt</Th>}
            </Tr>
          </THead>
          <TBody>
            {usage.byKind.map((k) => (
              <Tr key={k.kind}>
                <Td className="h-11">{aiJobKindLabel(k.kind)}</Td>
                <Td className="h-11 tabular" align="right">
                  {k.jobs}
                </Td>
                {showMoney && (
                  <Td className="h-11 tabular" align="right">
                    {formatAiCost(k.costPln)}
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {usage.scope === 'workspace' && usage.byPerson.length > 0 && (
        <Table aria-label="Zużycie według osób">
          <THead>
            <Tr>
              <Th>Osoba</Th>
              <Th align="right">Zadania</Th>
              {showMoney && <Th align="right">Koszt</Th>}
              {showMoney && <Th align="right">Limit / mies.</Th>}
            </Tr>
          </THead>
          <TBody>
            {usage.byPerson.map((p) => (
              <Tr key={p.userId}>
                <Td className="h-11">{p.name}</Td>
                <Td className="h-11 tabular" align="right">
                  {p.jobs}
                </Td>
                {showMoney && (
                  <Td className="h-11 tabular" align="right">
                    {formatAiCost(p.costPln)}
                  </Td>
                )}
                {showMoney && (
                  <Td className="h-11 tabular text-fg-muted" align="right">
                    {p.limitPln ? formatAiCost(p.limitPln) : 'bez limitu'}
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {usage.jobs === 0 && <p className="text-body-s text-fg-muted">W tym miesiącu AI nie było używane.</p>}
    </>
  );
}
